import { useMemo, useRef } from "react";
import type { RegisterOptions } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import type { Option } from "@circle-react-shared/RadioButtonGroup";
import { SegmentedControl } from "@circle-react-shared/SegmentedControl";

interface FormSegmentedControlProps {
  checkedOptionClassName?: string;
  hasCheckedIcon?: boolean;
  name: string;
  onChange?: (value: string) => void;
  options: Option[];
  rules?: RegisterOptions;
  size?: "sm" | "md";
  uncheckedOptionClassName?: string;
  wrapperClassName?: string;
}

export const FormSegmentedControl = ({
  name,
  options,
  onChange,
  rules = {},
  size = "sm",
  wrapperClassName,
  checkedOptionClassName,
  uncheckedOptionClassName,
  hasCheckedIcon = false,
}: FormSegmentedControlProps) => {
  const { register, setValue, getValues } = useFormContext();
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;

  const handleChange = useMemo(
    () => (newValue: string) => {
      setValueRef.current(name, newValue);
      onChangeRef.current?.(newValue);
    },
    [name],
  );

  const defaultValue = (getValues(name) || options[0]?.value) as string;

  return (
    <>
      <input type="hidden" {...register(name, rules)} />
      <SegmentedControl
        name={`${name}-display`}
        options={options}
        defaultChecked={defaultValue}
        onChange={handleChange}
        size={size}
        wrapperClassName={wrapperClassName}
        checkedOptionClassName={checkedOptionClassName}
        uncheckedOptionClassName={uncheckedOptionClassName}
        hasCheckedIcon={hasCheckedIcon}
      />
    </>
  );
};

FormSegmentedControl.displayName = "Form.SegmentedControl";
